/* Someone else pays for a student's pass, through the student's payment link. No account needed: the link is the key.
   /pay?token=           the plan, its price for the student's price group, and Pay (Stripe Checkout, a guest payment)
   /pay/done?token=      back from Stripe: waits for the payment to be confirmed
   /pay/withdraw?token=  the withdrawal link in a payer's confirmation email from before 1 November 2026: it shows the
                         purchase. Every sale is final since then, so it says so (can_withdraw is false); the 14-day
                         "Withdraw from contract here", then "Confirm withdrawal", shows only if the server allows it.
   Before paying, the payer ticks the box: the pass starts as soon as they pay, and all sales are final. */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { isLang, needsStartNow, planById, type CheckoutResponse, type PayInfo, type PayerPass } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { replaceUrl, useLocation } from '../app/nav';
import { focusHeading } from '../app/hooks';
import { Icon } from '../app/icons';
import { AiTag, Alert, Button, Checkbox, Live, Loading, PageHeader, Section, SelectField, TextField } from '../app/ui';
import { isAiFeature } from '../content/legal';
import { api, ApiError } from '../lib/api';
import { countryOptions, formatDate, formatMoney, paymentPageUrl } from '../lib/format';
import { looksLikeEmail } from '../lib/forms';
import { monthsText } from '../lib/i18n';
import { readLang } from '../lib/storage';
import { publicError, useNoIndex } from './Family';

const POLL_MS = 2000, POLL_TRIES = 30;
type LoadState = 'loading' | 'ok' | 'invalid' | 'error';
const gone = (e: unknown) => e instanceof ApiError && (e.code === 'not_found' || e.code === 'invalid' || e.status === 404);

/* the plan, as the payer sees it: GET /api/family/pay in the page's language */
function usePayInfo(token: string) {
  const { lang, setLang } = useT();
  const [info, setInfo] = useState<PayInfo | null>(null);
  const [load, setLoad] = useState<LoadState>(token ? 'loading' : 'invalid');
  const [n, setN] = useState(0);
  const first = useRef(true);
  useEffect(() => {
    if (!token) { setLoad('invalid'); return; }
    const ac = new AbortController();
    api.get<PayInfo>(`/api/family/pay?token=${encodeURIComponent(token)}&lang=${lang}`, { signal: ac.signal })
      .then(i => {
        setInfo(i);
        setLoad('ok');
        /* the page opens in the language the student chose for the link, unless this browser has a chosen language */
        if (first.current) { first.current = false; if (!readLang() && isLang(i.lang) && i.lang !== lang) setLang(i.lang, { remember: false }); }
      })
      .catch(e => { if (!ac.signal.aborted) setLoad(gone(e) ? 'invalid' : 'error'); });
    return () => ac.abort();
  }, [token, lang, n, setLang]);
  return { info, load, retry: () => setN(x => x + 1) };
}

function Problem({ load, retry, title }: { load: LoadState; retry: () => void; title: string }) {
  const { t } = useT();
  if (load === 'loading') return <div className="narrow"><PageHeader title={title} /><Loading /></div>;
  if (load === 'invalid') return <div className="narrow"><PageHeader title={t('pay.invalid.title')} lead={t('pay.invalid.text')} /></div>;
  return (
    <div className="narrow">
      <PageHeader title={title} />
      <Alert>{t('err.network')} <button type="button" className="linkbtn" onClick={retry}>{t('common.retry')}</button></Alert>
    </div>
  );
}

/* ---------- /pay ---------- */
export function Pay() {
  const { t, lang } = useT();
  const { query, pathname } = useLocation();
  const token = query.get('token') ?? '';
  const [canceled] = useState(() => query.get('canceled') === '1');
  useEffect(() => { if (canceled) replaceUrl(`${pathname}?token=${encodeURIComponent(token)}`); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useNoIndex();
  const { info, load, retry } = usePayInfo(token);
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [startNow, setStartNow] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; country?: string; startNow?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  if (load !== 'ok' || !info) return <Problem load={load} retry={retry} title={t('pay.title')} />;
  const plan = info.plan, name = info.student;
  const planName = plan.name || planById(plan.id).name[lang];
  const price = formatMoney(plan.price_usd, lang);
  const askStartNow = needsStartNow([country]);
  const title = name ? t('pay.titleName', { name, plan: planName }) : t('pay.title');

  if (info.status !== 'open') {
    return (
      <div className="narrow">
        <PageHeader title={title} />
        <Alert tone={info.status === 'paid' ? 'success' : 'info'} title={t(info.status === 'paid' ? 'pay.paid.title' : 'pay.covered.title')}>
          <p>{t(info.status === 'paid' ? 'pay.paid.text' : 'pay.covered.text')}</p>
        </Alert>
      </div>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: typeof errors = {};
    if (!looksLikeEmail(email.trim())) errs.email = t('v.email');
    if (!country) errs.country = t('v.choose');
    if (askStartNow && !startNow) errs.startNow = t('v.tick');
    setErrors(errs);
    const firstBad = errs.email ? 'pay-email' : errs.country ? 'pay-country' : errs.startNow ? 'pay-start-now' : null;
    if (firstBad) { document.getElementById(firstBad)?.focus(); return; }
    setBusy(true);
    try {
      const r = await api.post<CheckoutResponse>('/api/family/pay/checkout', { token, email: email.trim(), country, start_now: askStartNow ? startNow : false, lang });
      const url = paymentPageUrl(r.url);
      if (!url) throw new ApiError(500, 'server_error');
      setRedirecting(true);
      window.location.assign(url);
    } catch (er) {
      setBusy(false);
      if (er instanceof ApiError && er.code === 'withdrawal_ack_required') { setErrors({ startNow: t('v.tick') }); return; }
      if (er instanceof ApiError && er.code === 'already_paid') { retry(); return; }
      if (gone(er) && !(er instanceof ApiError && er.code === 'invalid')) { retry(); return; }
      setErrors({ form: publicError(er, lang) });
    }
  };

  return (
    <div className="narrow-wide">
      <PageHeader title={title} lead={name ? t('pay.lead', { name, plan: planName }) : t('pay.leadNoName', { plan: planName })} />
      {canceled && <div className="mb"><Alert tone="info">{t('pay.canceled')}</Alert></div>}

      <Section title={planName} id="pay-plan">
        <article className="plan" aria-labelledby="pay-plan-title">
          <div className="plan__head">
            <p className="plan__name">{t('pay.price')}</p>
            <p className="plan__price" dir="ltr">{price}</p>
          </div>
          <p className="plan__note">{t('pay.oneTime')} {t('pay.valid', { months: monthsText(plan.valid_months, lang) })}</p>
          <h3 className="card__title mt-sm">{t('pay.includes')}</h3>
          <ul className="includes">{plan.includes.map((item, i) => <li key={i}><Icon.check /><span>{item}{isAiFeature(item) && <> <AiTag /></>}</span></li>)}</ul>
        </article>
        <p className="refund"><Icon.info />{t('pay.refund')}</p>
      </Section>

      <Section title={t('pay.formTitle')} id="pay-form">
        <form className="card stack" noValidate onSubmit={submit} aria-labelledby="pay-form-title">
          <TextField id="pay-email" label={t('pay.email')} hint={t('pay.emailHint')} type="email" autoComplete="email" inputMode="email" ltr
            autoCapitalize="none" spellCheck={false} value={email} onChange={setEmail} error={errors.email} />
          <SelectField id="pay-country" label={t('pay.country')} value={country} onChange={v => { setCountry(v); setStartNow(false); }} error={errors.country}
            options={countryOptions(lang).map(c => ({ value: c.code, label: c.name }))} />
          {askStartNow && (
            <div className="startnow">
              <h3 className="card__title">{t('pay.startNowTitle')}</h3>
              <Checkbox id="pay-start-now" checked={startNow} onChange={v => { setStartNow(v); if (v) setErrors(x => ({ ...x, startNow: undefined })); }}
                label={t('pay.startNow')} description={t('pay.startNowNote')} error={errors.startNow} required />
            </div>
          )}
          {errors.form && <Alert>{errors.form}</Alert>}
          <Live>{redirecting ? <p className="hint">{t('plans.redirecting')}</p> : null}</Live>
          <Button type="submit" block busy={busy}>{t('pay.submit', { price })}</Button>
          <p className="fine">{t('pay.secure')}</p>
        </form>
      </Section>
    </div>
  );
}

/* ---------- /pay/done ---------- */
export function PayDone() {
  const { t, lang } = useT();
  const { query } = useLocation();
  const token = query.get('token') ?? '';
  useNoIndex();
  const [state, setState] = useState<'waiting' | 'ready' | 'slow' | 'invalid'>(token ? 'waiting' : 'invalid');
  const [plan, setPlan] = useState('');
  useEffect(() => {
    if (!token) return;
    let tries = 0, timer = 0, stop = false;
    const tick = async () => {
      tries++;
      try {
        const i = await api.get<PayInfo>(`/api/family/pay?token=${encodeURIComponent(token)}&lang=${lang}`);
        if (stop) return;
        if (i.status === 'paid') { setPlan(i.plan.name); setState('ready'); return; }
      } catch (e) {
        if (stop) return;
        if (gone(e)) { setState('invalid'); return; }
      }
      if (tries >= POLL_TRIES) { setState('slow'); return; }
      timer = window.setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
    return () => { stop = true; window.clearTimeout(timer); };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  if (state === 'invalid') return <div className="narrow"><PageHeader title={t('pay.invalid.title')} lead={t('pay.invalid.text')} /></div>;
  return (
    <div className="narrow">
      <PageHeader title={t('pay.done.title')} />
      <Live>
        {state === 'waiting' ? <Loading label={t('pay.done.waiting')} />
          : <Alert tone={state === 'ready' ? 'success' : 'warn'}>{state === 'ready' ? t('pay.done.ready', { plan }) : t('pay.done.slow')}</Alert>}
      </Live>
    </div>
  );
}

/* ---------- /pay/withdraw ---------- */
export function PayWithdraw() {
  const { t, lang, setLang } = useT();
  const { query } = useLocation();
  const token = query.get('token') ?? '';
  useNoIndex();
  const [pass, setPass] = useState<PayerPass | null>(null);
  const [load, setLoad] = useState<LoadState>(token ? 'loading' : 'invalid');
  const [n, setN] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!token) return;
    const ac = new AbortController();
    api.get<PayerPass>(`/api/family/withdraw?token=${encodeURIComponent(token)}`, { signal: ac.signal })
      .then(p => { setPass(p); setLoad('ok'); if (!readLang() && isLang(p.lang)) setLang(p.lang, { remember: false }); })
      .catch(e => { if (!ac.signal.aborted) setLoad(gone(e) ? 'invalid' : 'error'); });
    return () => ac.abort();
  }, [token, n, setLang]);
  useEffect(() => { if (confirming) confirmRef.current?.querySelector<HTMLElement>('h2')?.focus(); }, [confirming]);

  if (load === 'invalid') return <div className="narrow"><PageHeader title={t('pay.wd.title')} lead={t('pay.wd.invalid')} /></div>;
  if (load !== 'ok' || !pass) return <Problem load={load} retry={() => setN(x => x + 1)} title={t('pay.wd.title')} />;

  const planName = planById(pass.plan).name[lang];
  const price = pass.amount_cents != null && pass.currency ? `${(pass.amount_cents / 100).toFixed(2)} ${pass.currency.toUpperCase()}` : '';
  const lead = pass.student
    ? t('pay.wd.lead', { plan: planName, name: pass.student, date: formatDate(pass.paid_at, lang) })
    : t('pay.wd.leadNoName', { plan: planName, date: formatDate(pass.paid_at, lang) });

  const withdraw = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<PayerPass>('/api/family/withdraw', { token, confirm: true });
      setPass(r);
      setConfirming(false);
      requestAnimationFrame(() => focusHeading());
    } catch (e) {
      if (e instanceof ApiError && e.code === 'forbidden') { setConfirming(false); setN(x => x + 1); setError(e.serverMessage || t('pass.withdrawOver')); }
      else setError(publicError(e, lang));
    } finally { setBusy(false); }
  };

  return (
    <div className="narrow">
      <PageHeader title={t('pay.wd.title')} lead={lead} />
      {error && <div className="mb"><Alert>{error}</Alert></div>}
      {pass.status === 'refunded' ? (
        <Alert tone="success">{t('pay.wd.done', { date: formatDate(pass.refunded_at, lang) })}</Alert>
      ) : !pass.can_withdraw ? (
        <Alert tone="info">{t('pay.wd.over', { date: formatDate(pass.refundable_until, lang) })}</Alert>
      ) : !confirming ? (
        <div className="card stack">
          <p>{t('pay.wd.until', { date: formatDate(pass.refundable_until, lang) })}</p>
          <Button variant="danger" onClick={() => setConfirming(true)}>{t('pass.withdraw')}</Button>
        </div>
      ) : (
        <div className="card confirm" ref={confirmRef}>
          <h2 className="card__title" tabIndex={-1}>{t('pass.confirmTitle')}</h2>
          <ul className="ticks">
            <li>{t('pay.wd.confirm1', { price })}</li>
            <li>{t('pay.wd.confirm2')}</li>
            <li>{t('pay.wd.confirm3')}</li>
          </ul>
          <div className="btnrow">
            <Button variant="danger-solid" busy={busy} onClick={() => void withdraw()}>{t('pass.confirm')}</Button>
            <Button variant="gray" onClick={() => setConfirming(false)}>{t('pay.wd.keep')}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
